import type { MessageGroup } from '@mangovoice/types'

import {
  NodeType,
  type SwitchboardEdge,
  type SwitchboardFlowData,
  type SwitchboardNode,
} from '../converters/shared/reactFlowTypes'
import { convertReactFlowToLegacyFormat } from '../converters/toLegacy/index'
import { convertLegacyFormatToReactFlow } from '../converters/toReactFlow/index'
import { findFixedHandleRouteConflicts } from './fixedHandleRouteConflicts'
import { normalizePhoneForComparison } from './phoneNumberComparison'
import { findMessagingGroupBySendFrom } from './smsSendFromOptions'

export interface SignatureRefContext {
  messagingGroups?: MessageGroup[]
  messagingGroupsLoaded?: boolean
}

export type PersistedGraphSignature =
  { status: 'ready'; value: string } | { status: 'pending'; reason: string }

type SignatureMode = 'persisted' | 'content-data'

type ComparableNode = {
  id: string
  type: string
  position?: { x: number; y: number }
  width?: number | string
  height?: number | string
  data: Record<string, unknown>
}

type ComparableEdge = {
  source: string
  target: string
  sourceHandle: string | null
  targetHandle: string | null
  data: unknown
}

function toStableValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => toStableValue(item))
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>
    const sortedEntries = Object.keys(record)
      .sort()
      .filter((key) => record[key] !== undefined)
      .map((key) => [key, toStableValue(record[key])] as const)
    return Object.fromEntries(sortedEntries)
  }

  return value
}

function getPrimaryOrFirstPhone(group: MessageGroup) {
  return (
    group.phone_numbers?.find((phone) => phone.is_primary_sms === true) ??
    group.phone_numbers?.[0]
  )
}

function getCanonicalGroupPhoneValue(group: MessageGroup): string | undefined {
  const primaryPhone = getPrimaryOrFirstPhone(group)
  const normalizedGroupPhone =
    normalizePhoneForComparison(primaryPhone?.number) ||
    normalizePhoneForComparison(primaryPhone?.number_for_display)

  return normalizedGroupPhone ? `phone:${normalizedGroupPhone}` : undefined
}

function getUnresolvedSmsSendFromValue(rawValue: string): string {
  const normalizedPhone = normalizePhoneForComparison(rawValue)
  return `__unresolved_sms_sendFrom__:${normalizedPhone || rawValue}`
}

function canonicalizeSmsSendFrom(
  sendFrom: unknown,
  refContext: SignatureRefContext | undefined,
  mode: SignatureMode,
): { value?: string; pending: boolean } {
  if (sendFrom === undefined || sendFrom === null || sendFrom === '') {
    return { value: undefined, pending: false }
  }

  const rawValue = String(sendFrom).trim()
  if (rawValue === '') {
    return { value: undefined, pending: false }
  }
  const messagingGroupsLoaded = refContext?.messagingGroupsLoaded === true
  const messagingGroups = refContext?.messagingGroups ?? []

  if (messagingGroupsLoaded) {
    const matchingGroup = findMessagingGroupBySendFrom(
      messagingGroups,
      rawValue,
    )
    if (matchingGroup) {
      const canonicalGroupPhone = getCanonicalGroupPhoneValue(matchingGroup)
      if (canonicalGroupPhone) {
        return {
          value: canonicalGroupPhone,
          pending: false,
        }
      }

      return {
        value: `messaging-group:${matchingGroup.id}`,
        pending: false,
      }
    }

    const normalizedPhone = normalizePhoneForComparison(rawValue)
    return {
      value: normalizedPhone ? `phone:${normalizedPhone}` : rawValue,
      pending: false,
    }
  }

  return {
    value: getUnresolvedSmsSendFromValue(rawValue),
    pending: mode === 'persisted',
  }
}

/**
 * Semantic fields worth keeping on a preserved (unconnected) endpoint. Generated
 * identifiers (`uuid`, `source_uuid`, `target_uuid`) are intentionally dropped
 * because legacy conversion can synthesize them differently for structurally
 * identical graphs, which would otherwise make equivalent flows hash apart.
 */
const STABLE_ENDPOINT_FIELDS = ['anchor', 'my_label', 'option_id'] as const

/**
 * Endpoint-array keys that hold preserved legacy endpoint metadata. These arrays
 * carry generated UUIDs that must not influence the signature.
 */
const ENDPOINT_ARRAY_KEYS = [
  'unconnectedSourceEndpoints',
  'unconnectedTargetEndpoints',
  'unconnected_source_endpoints',
  'unconnected_target_endpoints',
] as const

function canonicalizeEndpointArray(value: unknown): unknown {
  if (!Array.isArray(value)) {
    return value
  }

  return (
    value
      .map((endpoint) => {
        if (!endpoint || typeof endpoint !== 'object') {
          return endpoint
        }
        const record = endpoint as Record<string, unknown>
        const canonical: Record<string, unknown> = {}
        for (const field of STABLE_ENDPOINT_FIELDS) {
          if (record[field] !== undefined) {
            canonical[field] = record[field]
          }
        }
        return canonical
      })
      // Drop endpoints that carry no semantic fields (only generated identifiers).
      // Such endpoints are indistinguishable from an absent endpoint for graph
      // equivalence, so they must not perturb the signature.
      .filter(
        (endpoint) =>
          !endpoint ||
          typeof endpoint !== 'object' ||
          Object.keys(endpoint as Record<string, unknown>).length > 0,
      )
      .sort((a, b) => {
        const left = JSON.stringify(toStableValue(a))
        const right = JSON.stringify(toStableValue(b))
        return left.localeCompare(right)
      })
  )
}

function dedupeEndpointArrayAliases(
  data: Record<string, unknown>,
  camelKey: 'unconnectedSourceEndpoints' | 'unconnectedTargetEndpoints',
  snakeKey: 'unconnected_source_endpoints' | 'unconnected_target_endpoints',
): void {
  if (!(camelKey in data) || !(snakeKey in data)) {
    return
  }

  const camelValue = stringifySignatureValue(data[camelKey])
  const snakeValue = stringifySignatureValue(data[snakeKey])
  if (camelValue === snakeValue) {
    delete data[snakeKey]
  }
}

function normalizeNodeData(
  node: SwitchboardNode,
  refContext: SignatureRefContext | undefined,
  mode: SignatureMode,
): { data: Record<string, unknown>; pendingReasons: string[] } {
  const data =
    node.data && typeof node.data === 'object'
      ? { ...(node.data as Record<string, unknown>) }
      : {}
  const pendingReasons: string[] = []

  delete data.validationErrors

  // Generated endpoint identifiers are not semantically meaningful: legacy
  // conversion can synthesize them differently for equivalent graphs. Strip
  // top-level ones and canonicalize the preserved endpoint arrays so equivalent
  // flows produce identical signatures (prevents a pinned snapshot / false
  // conflict from UUID churn alone).
  delete data.uuid
  delete data.source_uuid
  delete data.target_uuid

  for (const key of ENDPOINT_ARRAY_KEYS) {
    if (key in data) {
      const canonical = canonicalizeEndpointArray(data[key])
      if (Array.isArray(canonical) && canonical.length === 0) {
        delete data[key]
      } else {
        data[key] = canonical
      }
    }
  }
  dedupeEndpointArrayAliases(
    data,
    'unconnectedSourceEndpoints',
    'unconnected_source_endpoints',
  )
  dedupeEndpointArrayAliases(
    data,
    'unconnectedTargetEndpoints',
    'unconnected_target_endpoints',
  )

  if (node.type === NodeType.SmsNotification && 'sendFrom' in data) {
    const normalizedSendFrom = canonicalizeSmsSendFrom(
      data.sendFrom,
      refContext,
      mode,
    )
    if (normalizedSendFrom.value === undefined) {
      delete data.sendFrom
    } else {
      data.sendFrom = normalizedSendFrom.value
    }
    if (normalizedSendFrom.pending) {
      pendingReasons.push('sms_notification.sendFrom')
    }
  }
  if (node.type === NodeType.SmsNotification && 'sms_number' in data) {
    const smsNumber =
      data.sms_number === null || data.sms_number === undefined
        ? undefined
        : String(data.sms_number)
    const normalizedSmsNumber = normalizePhoneForComparison(smsNumber)
    if (normalizedSmsNumber) {
      data.sms_number = `phone:${normalizedSmsNumber}`
    }
  }

  return { data, pendingReasons }
}

function normalizeFlowForSignature(
  flowData: SwitchboardFlowData,
  mode: SignatureMode,
  refContext?: SignatureRefContext,
): {
  value: { nodes: ComparableNode[]; edges: ComparableEdge[] }
  pendingReasons: string[]
} {
  const pendingReasons: string[] = []

  const nodes = flowData.nodes
    .map<ComparableNode>((node) => {
      const normalizedData = normalizeNodeData(node, refContext, mode)
      pendingReasons.push(...normalizedData.pendingReasons)

      const comparableNode: ComparableNode = {
        id: node.id,
        type: String(node.type),
        data: normalizedData.data,
      }

      if (mode === 'persisted') {
        comparableNode.position = {
          x: node.position?.x ?? 0,
          y: node.position?.y ?? 0,
        }
        comparableNode.width =
          typeof node.width === 'number' || typeof node.width === 'string'
            ? node.width
            : undefined
        comparableNode.height =
          typeof node.height === 'number' || typeof node.height === 'string'
            ? node.height
            : undefined
      }

      return comparableNode
    })
    .sort((a, b) => a.id.localeCompare(b.id))

  const edges = flowData.edges
    .map<ComparableEdge>((edge: SwitchboardEdge) => ({
      source: edge.source,
      target: edge.target,
      sourceHandle: edge.sourceHandle ?? null,
      targetHandle: edge.targetHandle ?? null,
      data: edge.data,
    }))
    .sort((a, b) => {
      const left = `${a.source}:${a.sourceHandle ?? ''}:${a.target}:${a.targetHandle ?? ''}`
      const right = `${b.source}:${b.sourceHandle ?? ''}:${b.target}:${b.targetHandle ?? ''}`
      return left.localeCompare(right)
    })

  return {
    value: { nodes, edges },
    pendingReasons: Array.from(new Set(pendingReasons)).sort(),
  }
}

function stringifySignatureValue(value: unknown): string {
  return JSON.stringify(toStableValue(value))
}

function clonePlainValue<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

function getSaveEquivalentFlowData(
  flowData: SwitchboardFlowData,
  refContext?: SignatureRefContext,
): SwitchboardFlowData {
  const messagingGroupsForSave =
    refContext?.messagingGroupsLoaded === true
      ? refContext.messagingGroups
      : undefined
  const legacyData = convertReactFlowToLegacyFormat(
    clonePlainValue(flowData),
    messagingGroupsForSave,
  )

  return convertLegacyFormatToReactFlow(clonePlainValue(legacyData))
}

function normalizePersistedGraphForSignature(
  flowData: SwitchboardFlowData,
  refContext?: SignatureRefContext,
): {
  value: { nodes: ComparableNode[]; edges: ComparableEdge[] }
  pendingReasons: string[]
} {
  if (findFixedHandleRouteConflicts(flowData).length > 0) {
    return normalizeFlowForSignature(flowData, 'persisted', refContext)
  }

  return normalizeFlowForSignature(
    getSaveEquivalentFlowData(flowData, refContext),
    'persisted',
    refContext,
  )
}

export function getPersistedGraphSignature(
  flowData: SwitchboardFlowData,
  refContext?: SignatureRefContext,
): PersistedGraphSignature {
  const normalized = normalizePersistedGraphForSignature(flowData, refContext)
  if (normalized.pendingReasons.length > 0) {
    return {
      status: 'pending',
      reason: normalized.pendingReasons.join(','),
    }
  }

  return {
    status: 'ready',
    value: stringifySignatureValue(normalized.value),
  }
}

export function getPersistedGraphComparableSignature(
  flowData: SwitchboardFlowData,
  refContext?: SignatureRefContext,
): string {
  const normalized = normalizePersistedGraphForSignature(flowData, refContext)
  return stringifySignatureValue(normalized.value)
}

export function getContentDataSignature(
  flowData: SwitchboardFlowData,
  refContext?: SignatureRefContext,
): string {
  const normalized = normalizeFlowForSignature(
    flowData,
    'content-data',
    refContext,
  )
  return stringifySignatureValue(normalized.value)
}

export function getReadyPersistedGraphSignature(
  flowData: SwitchboardFlowData,
  refContext?: SignatureRefContext,
): string | null {
  const signature = getPersistedGraphSignature(flowData, refContext)
  return signature.status === 'ready' ? signature.value : null
}

export function hasPersistedGraphChanges(
  previousFlowData: SwitchboardFlowData,
  nextFlowData: SwitchboardFlowData,
  refContext?: SignatureRefContext,
): boolean {
  return (
    getPersistedGraphComparableSignature(previousFlowData, refContext) !==
    getPersistedGraphComparableSignature(nextFlowData, refContext)
  )
}
